/*!
 * This module handles *ranking* drag-and-drop of epic cards to re-order them.
 */
import { type MutableRefObject, useCallback, useState, useLayoutEffect } from 'react';
import {
	type Edge,
	attachClosestEdge,
	extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
	draggable,
	dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import {
	fireOperationalAnalytics,
	fireTrackAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useAutobatch } from '@atlassian/jira-software-react-scheduler/src/ui/autobatch/index.tsx';
import { DND_OPERATION_EPIC_RANK_UPDATE } from '../../../../../common/constants.tsx';
import { useRankEpicsMutation } from '../../../../../services/epic/rank/index.tsx';
import { useRankExperienceTracker } from '../../../../../services/experience-tracker/index.tsx';
import {
	cancelBacklogPanelEpicRankInteraction,
	setBacklogPanelEpicRankInteractionStartMark,
	stopBacklogPanelEpicRankInteraction,
} from '../../../../../services/performance/metrics-interaction-epic-rank/index.tsx';

export function useRankEpic(
	boardId: number,
	isCompanyManaged: boolean,
): (payload: { epicId: number; index: number }) => void {
	const [rankEpics] = useRankEpicsMutation(boardId);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		fireExperienceStart: fireRankExperienceStart,
		fireExperienceSuccess: fireRankExperienceSuccess,
		fireExperienceError: fireRankExperienceError,
	} = useRankExperienceTracker({
		isClassic: isCompanyManaged,
	});

	const analyticsEvent = createAnalyticsEvent({
		action: 'droppedWithin',
		actionSubject: 'card',
	});

	return useCallback(
		({ epicId, index }) => {
			if (!epicId) return;

			setBacklogPanelEpicRankInteractionStartMark();
			fireRankExperienceStart();

			rankEpics([epicId], index)
				.then(() => {
					stopBacklogPanelEpicRankInteraction(isCompanyManaged);
					fireRankExperienceSuccess();
					fireTrackAnalytics(analyticsEvent, 'issueRank updated');
				})
				.catch((error) => {
					cancelBacklogPanelEpicRankInteraction();
					fireRankExperienceError(error);
					// only support signal card ranking in epic panel
					const numOfSelectedCards = 1;
					fireOperationalAnalytics(analyticsEvent, 'cards moveFailed', {
						numCards: numOfSelectedCards,
						errorMessage: error.message,
						operation: DND_OPERATION_EPIC_RANK_UPDATE,
						isClientFetchError: isClientFetchError(error),
					});
				});
		},
		[
			isCompanyManaged,
			rankEpics,
			analyticsEvent,
			fireRankExperienceError,
			fireRankExperienceStart,
			fireRankExperienceSuccess,
		],
	);
}

interface UseDragToRankEpicParams {
	ref: MutableRefObject<HTMLElement | null> | null;
	epicId: string;
	index: number;
	isDropAllowed: () => boolean;
	rankEpic: ReturnType<typeof useRankEpic>;
}

type DragState = { type: 'idle' } | { type: 'dragging'; container: HTMLElement };

export function useDragToRankEpic({
	ref,
	epicId,
	index,
	isDropAllowed,
	rankEpic,
}: UseDragToRankEpicParams): {
	closestEdge: Edge | null;
	dragState: DragState;
} {
	const [dragState, setDragState] = useState<DragState>({
		type: 'idle',
	});
	const [closestEdge, setClosestEdge] = useState<Edge | null>(null);
	const autobatch = useAutobatch();
	useLayoutEffect(() => {
		if (!ref?.current) return undefined;

		return combine(
			draggable({
				element: ref.current,
				getInitialData: () => ({
					type: 'EPIC_CARD',
					epicId,
				}),
				// eslint-disable-next-line @typescript-eslint/no-empty-function
				onDragStart: () => {},
				onDrop: (_args) => {
					autobatch(() => {
						setDragState({ type: 'idle' });
					});
				},
				onGenerateDragPreview: ({ nativeSetDragImage }) => {
					setCustomNativeDragPreview({
						getOffset: pointerOutsideOfPreview({
							x: '-16px',
							y: '-16px',
						}),
						render({ container }) {
							setDragState({ type: 'dragging', container });
							return () => setDragState({ type: 'idle' });
						},
						nativeSetDragImage,
					});
				},
			}),
			dropTargetForElements({
				element: ref.current,
				getIsSticky: () => true,
				getData: ({ input, element }) => {
					const data = {
						type: 'EPIC_CARD',
						epicId,
					};

					return attachClosestEdge(data, {
						input,
						element,
						allowedEdges: ['top', 'bottom'],
					});
				},
				onDrag: (args) => {
					if (args.source.data.type === 'EPIC_CARD' && args.source.data.epicId !== epicId) {
						autobatch(() => {
							setClosestEdge(extractClosestEdge(args.self.data));
						});
					}
				},
				onDragEnter: (args) => {
					if (args.source.data.type === 'EPIC_CARD' && args.source.data.epicId !== epicId) {
						autobatch(() => {
							setClosestEdge(extractClosestEdge(args.self.data));
						});
					}
				},
				onDragLeave: (args) => {
					if (args.source.data.type === 'EPIC_CARD') {
						autobatch(() => {
							setClosestEdge(null);
						});
					}
				},
				// eslint-disable-next-line @typescript-eslint/no-empty-function
				onDropTargetChange: (_args) => {},
				canDrop: (args) =>
					args.source.data.type === 'EPIC_CARD' &&
					args.source.data.epicId !== epicId &&
					isDropAllowed(),
				onDrop: (args) => {
					const sourceEpicId = Number(args.source.data.epicId);
					const targetEpicId = Number(args.self.data.epicId);
					autobatch(() => {
						setClosestEdge(null);
						if (sourceEpicId !== targetEpicId) {
							rankEpic({ epicId: sourceEpicId, index });
						}
					});
				},
			}),
		);
	}, [ref, epicId, rankEpic, index, isDropAllowed, autobatch]);

	return {
		dragState,
		closestEdge,
	};
}
